/**
 * Role-based access control constants and utilities
 */

// Role IDs
export const ROLES = {
  OWNER: 1,
  DISTRIBUTOR: 2,
  RESELLER: 3,
  CUSTOMER: 4
} as const;

// Role names
export const ROLE_NAMES: Record<number, string> = {
  [ROLES.OWNER]: 'Owner',
  [ROLES.DISTRIBUTOR]: 'Distributor',
  [ROLES.RESELLER]: 'Reseller',
  [ROLES.CUSTOMER]: 'Customer'
};

// Get user role from session storage
export const getUserRole = (): number => {
  const userStr = sessionStorage.getItem('user');
  if (!userStr) return ROLES.CUSTOMER; // Default to lowest permission level
  
  try {
    const user = JSON.parse(userStr);
    return user.role_id || ROLES.CUSTOMER;
  } catch (error) {
    console.error('Failed to parse user data:', error);
    return ROLES.CUSTOMER;
  }
};

// Get user company ID from session storage
export const getUserCompanyId = (): number | null => {
  const userStr = sessionStorage.getItem('user');
  if (!userStr) return null;
  
  try {
    const user = JSON.parse(userStr);
    return user.company_id || null;
  } catch (error) {
    console.error('Failed to parse user data:', error);
    return null;
  }
};

// Get complete user data from session storage
export const getCurrentUser = (): any | null => {
  const userStr = sessionStorage.getItem('user');
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Failed to parse user data:', error);
    return null;
  }
};

// Role permission checks
export const isOwner = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.OWNER;
};

export const isDistributor = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.DISTRIBUTOR;
};

export const isReseller = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.RESELLER;
};

export const isCustomer = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.CUSTOMER;
};

// Check if user has minimum role level
export const hasMinimumRole = (minimumRoleId: number, roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role <= minimumRoleId; // Lower role_id = higher permission
};

// Feature permissions
export const canAccessAnalytics = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.OWNER; // Only Owner can access Analytics
};

export const canManageProducts = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.OWNER; // Only Owner can manage Products & Plans
};

export const canCreateSubscriptions = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.OWNER; // Only Owner can create subscriptions
};

export const canPlaceOrders = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  // All roles can place orders (Owner, Distributor, Reseller, Customer)
  return role === ROLES.OWNER || role === ROLES.DISTRIBUTOR || role === ROLES.RESELLER || role === ROLES.CUSTOMER;
};

export const canManageResellers = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.OWNER || role === ROLES.DISTRIBUTOR;
};

export const canManageCustomers = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.OWNER || role === ROLES.DISTRIBUTOR || role === ROLES.RESELLER;
};

export const canManageCampaigns = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.OWNER || role === ROLES.DISTRIBUTOR || role === ROLES.RESELLER;
};

export const canAddCredits = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role === ROLES.OWNER || role === ROLES.DISTRIBUTOR || role === ROLES.RESELLER;
};

// Customer-specific permissions
export const canViewOwnSubscriptions = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  // All roles can view subscriptions (filtered by role on backend)
  return true;
};

export const canViewOwnOrders = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  // All roles can view orders (filtered by role on backend)
  return true;
};

// Data filtering helpers
export const shouldFilterByCompany = (roleId?: number): boolean => {
  const role = roleId ?? getUserRole();
  return role !== ROLES.OWNER; // Everyone except Owner sees filtered data
};

export const getCompanyFilter = (): { company_id?: number } => {
  if (shouldFilterByCompany()) {
    const companyId = getUserCompanyId();
    return companyId ? { company_id: companyId } : {};
  }
  return {};
};
